import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const StatusContext = createContext();
 
export const useStatuses = () => {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatuses must be used within a StatusProvider');
  }
  return context;
};

export const StatusProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [setApi] = useState(null);
  const { handleApiError } = useError();
  const [statuses, setStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/statuses');

  const getStatuses = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }

      if(filters?.type){
        query += '&type='+filters?.type;
      } else{
        const type = searchParams.get('type') || '';
        query += '&type='+type;
      }
      
      let response = await apiInstance.call('statuses?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          query += '&active='+filters?.active;
        } else{
          const active = searchParams.get('active') || '';
          query += '&active='+active;
        }
        if(filters?.type){
          query += '&type='+filters?.type;
        } else{
          const type = searchParams.get('type') || '';
          query += '&type='+type;
        }

        response = await apiInstance.call('statuses?page=' + 0 +''+query);  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setStatuses(response.data.statuses || []);
       
        if(filters){
          return response.data.statuses;
        }
      } else {
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch statuses information'
        });
      }
    } catch (error) {
      // Handle error silently
    } finally {
      // Cleanup if needed
    }
  }, [handleApiError, paginate, getToken]);

  const getStatusByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`statuses/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      let statuses = {'data':[response.data.status]}
      setStatuses(statuses || []);
    } 
  }, [getToken]);

  useEffect(() => {
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/statuses/edit/'+id))){
        if (getToken()) {
          await getStatusByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getStatusByIdApi, searchParams, setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getStatuses(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getStatuses, searchParams, initialized, hideLoading, setApi]);

  const getStatusesAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-statuses-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get statuses data'
      });
    } 
  }, [getToken, handleApiError]);

  const addStatus = async (data) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...data };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('statuses', 'POST', formData, true, true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the statuses list
        await getStatuses();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Status'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create status'
      });
      return { status: 500, error: error.message };
    }
  };

  const getStatus = (id) => {
    return statuses?.data?.find(status => Number(status.id) === Number(id));
  };

  const getStatusById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`statuses/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.status };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch status details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch status details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateStatus = async (id, updatedData) => {
    setIsLoading(true);
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      setIsLoading(false);
      return;
    }

    try {
      let formData = new FormData();
      
      // Remove any file fields from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      formData.append('_method', 'PUT');

      const response = await apiInstance.call(`statuses/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the statuses list
        await getStatuses();
        setIsLoading(false);
        return { status: response.status, data: response.data };
      } else {
        setIsLoading(false);
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update status'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update status'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteStatus = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`statuses/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the statuses list
        await getStatuses();
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete status'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete status'
      });
      return { status: 500, error: error.message };
    }
  };

  const value = {
    statuses,
    getStatuses,
    getStatusById,
    getStatus,
    addStatus,
    createStatus: addStatus,
    updateStatus,
    deleteStatus,
    getStatusesAdditionalData,
    isLoading
  };

  return (
    <StatusContext.Provider value={value}>
      {children}
    </StatusContext.Provider>
  );
};

export default StatusContext;
